import styles from '../auth/login.module.scss';
import { useRef } from 'react';
import {
    Box,
    Typography,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Link
} from '@mui/material';

const sections = [
    { id: 'privacy', title: 'Privacy Policy' },
    { id: 'intro', title: '1. Introduction' },
    { id: 'info', title: '2. Information We Collect' },
    { id: 'use', title: '3. How We Use Your Information' },
    { id: 'storage', title: '4. Data Storage & Security' },
    { id: 'sharing', title: '5. Sharing of Information' },
    { id: 'retention', title: '6. Data Retention & Deletion' },
    { id: 'rights', title: '7. Your Rights & Choices' },
    { id: 'third', title: '8. Third-Party Services & External Links' },
    { id: 'apple', title: '9. Apple App Store Review & Compliance' },
    { id: 'changes', title: '10. Changes to This Privacy Policy' },
    { id: 'contact', title: '11. Contact Information' }
];

export const PrivacyPolicy = () => {
    // const [activeSection, setActiveSection] = useState('intro');
    const contentRef = useRef(null);

    // useEffect(() => {
    //     const observer = new IntersectionObserver(
    //         (entries) => {
    //             const visible = entries.filter((e) => e.isIntersecting);
    //             if (visible.length > 0) {
    //                 setActiveSection(visible[0].target.id);
    //             }
    //         },
    //         {
    //             root: null,
    //             threshold: [0.5, 0.7],
    //             rootMargin: '-20% 0px -40% 0px',
    //         }
    //     );

    //     const elements = sections.map((s) => document.getElementById(s.id));
    //     elements.forEach((el) => el && observer.observe(el));
    //     return () => observer.disconnect();
    // }, []);

    const scrollToSection = (id) => {
        const container = contentRef.current;
        const target = document.getElementById(id);
        if (container && target) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const offset = targetRect.top - containerRect.top + container.scrollTop - 80;
            container.scrollTo({ top: offset, behavior: 'smooth' });
        }
    };

    return (
        <Grid
            className={`${styles.login}`}
            container
            sx={{
                width: '100%',
                height: '100vh',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            <Box className={styles['home-lights']}>
                <Box className={styles['light-wrap']}>
                    <Box
                        component="img"
                        width="1440"
                        alt=""
                        src={'public-purple-glimmer-effect.webp'}
                        className={styles["light-left"]}
                    />
                </Box>
            </Box>

            <Grid
                container
                maxWidth="md"
                sx={{
                    py: 6,
                    color: 'white',
                    marginX: 'auto',
                    height: '100vh',
                }}
            >
                {/* Sidebar */}
                <Grid item md={3} sx={{ pr: 2, display: { xs: 'none', md: 'block' } }}>
                    <List sx={{ position: 'sticky', top: 50 }} dense>
                        {sections.map((sec) => (
                            <ListItemButton
                                key={sec.id}
                                onClick={() => scrollToSection(sec.id)}
                                sx={{
                                    // color: activeSection === sec.id ? '#cd99e3ff' : 'white',
                                    // borderLeft: activeSection === sec.id ? '3px solid #cd99e3ff' : '3px solid transparent',
                                    color: 'white',
                                    borderLeft: '3px solid transparent',
                                    pl: 2,
                                    '&:hover': { color: '#cd99e3ff' },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                {sec.title}
                            </ListItemButton>
                        ))}
                    </List>
                </Grid>

                {/* Main Content */}
                <Grid
                    item
                    md={9}
                    ref={contentRef}
                    sx={{
                        pr: 2,
                        pl: { xs: 2, md: 0 },
                        overflowY: 'auto',
                        height: '100vh',
                        scrollBehavior: 'smooth',
                    }}
                >
                    <Typography id="privacy" variant="h4" gutterBottom fontWeight="bold">
                        Privacy Policy for CHARR Mobile App by Ikhala Impact
                    </Typography>
                    <Typography variant="subtitle2" gutterBottom>
                        Effective Date: 11 February 2025<br />
                        Last Updated: 11 February 2025
                    </Typography>

                    <Section id="intro" title="1. Introduction">
                        Welcome to Ikhala Impact (Pty) Ltd, the developer and manager of the CHARR App (“Company,” “we,” “our,” or “us”).
                        Our applications facilitate data collection related to biochar production, carbon footprinting, and sustainability efforts
                        in the agricultural and forestry sectors.
                        <br /><br />
                        This Privacy Policy explains how we collect, use, store, and protect your data when you use our:
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Biochar Data Collection Mobile App" /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Biochar Data Processing Features of the Web Application" /></ListItem>
                        </List>
                        By using our applications, you agree to the terms outlined in this Privacy Policy.
                    </Section>

                    <Section id="info" title="2. Information We Collect">
                        We collect and process the following types of data:
                        <br /><br />
                        <Typography fontWeight="bold">2.1 Personal Information</Typography>
                        <Typography paragraph>Collected when users register and interact with our applications:</Typography>
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Account Information: Name, email address, login credentials." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="User Preferences: Saved configurations and settings." /></ListItem>
                        </List>

                        <Typography fontWeight="bold" sx={{ mt: 2 }}>2.2 Data Collected Through the Biochar Mobile App</Typography>
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Location Data: GPS coordinates (if the user consents) for tracking biochar production sites." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Photos and Videos: Users may upload images and videos for documentation." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Offline Data Storage: Data is temporarily stored offline and synced when an internet connection is available. The user can then delete synced data once they have verified that data is reflecting on the web application." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Device Information: Device type, operating system, app version, and performance logs." /></ListItem>
                        </List>

                        <Typography fontWeight="bold" sx={{ mt: 2 }}>2.3 Data Collected Through the Biochar Web Apps</Typography>
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Process Data: Information related to fuel volumes, transport distances, equipment used, lab results." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="User Input: Data manually entered by users through forms and reports." /></ListItem>
                        </List>
                    </Section>

                    <Section id="use" title="3. How We Use Your Information">
                        We process collected data for the following purposes:
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Providing Services: Enabling users to collect, store, and analyze biochar data." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Data Syncing to AWS: Storing and securing user data in AWS-hosted cloud environments." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Regulatory Compliance: Ensuring compliance with applicable environmental, privacy, and data protection laws." /></ListItem>
                        </List>
                        We do not sell or rent user data to third parties.
                    </Section>

                    <Section id="storage" title="4. Data Storage & Security">
                        We take strong security measures to protect user data:
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Encryption: All data is encrypted during transmission." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="AWS Secure Storage: We store all data in a secure AWS cloud environment." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Access Control: Only authorized personnel can access user data." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Regular Security Audits: We review security practices regularly to ensure compliance with industry standards." /></ListItem>
                        </List>
                        We do not sell or rent your data to third parties.
                        <br /><br />
                        We keep your information only as long as necessary for the purposes outlined in this policy or as required by law.
                        We take reasonable measures to protect your information from unauthorised access, alteration, or disclosure. However, no method of transmission or storage is 100% secure.
                    </Section>

                    <Section id="sharing" title="5. Sharing of Information">
                        We may share user information only under these conditions:
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Regulatory Authorities: If required by law or regulatory compliance." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Service Providers: Data may be processed by cloud storage (AWS) and infrastructure partners to enable our app services." /></ListItem>
                        </List>
                        We do not share user data with advertisers or unrelated third-party services.
                    </Section>

                    <Section id="retention" title="6. Data Retention & Deletion">
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Retention Period: User data is retained only as long as necessary for its intended purposes or as required by law." /></ListItem>
                            <ListItem disablePadding>
                                <ListItemText>
                                    Users may request account deletion and data removal by contacting
                                    <Link href="mailto:wihan@mycharr.app" sx={{ color: '#cd99e3ff', textDecoration: 'none', mx: 0.5 }}>wihan@mycharr.app</Link>
                                    or
                                    <Link href="mailto:warwick@mycharr.app" sx={{ color: '#cd99e3ff', textDecoration: 'none', mx: 0.5 }}>warwick@mycharr.app</Link>.
                                </ListItemText>
                            </ListItem>
                        </List>
                        Upon user request, all personally identifiable information will be permanently erased unless legally required for retention.
                    </Section>

                    <Section id="rights" title="7. Your Rights & Choices">
                        Users may have the following rights, depending on their location:
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="Access & Download Data: Users can request a copy of their stored data." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Modify & Delete Data: Users can update or request deletion of their data." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Opt-Out of Tracking: Users can disable location tracking and media access at any time in the app settings." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Withdraw Consent: Users may revoke their consent for specific data collection features." /></ListItem>
                        </List>
                        To exercise these rights, contact:
                        <Link href="mailto:wihan@mycharr.app" sx={{ color: '#cd99e3ff', textDecoration: 'none', mx: 0.5 }}>wihan@mycharr.app</Link>.
                    </Section>

                    <Section id="third" title="8. Third-Party Services & External Links">
                        Our applications may contain links to third-party websites or services. We are not responsible for their privacy policies or data handling practices. Users should review the privacy policies of any third-party services before engaging with them.
                    </Section>

                    <Section id="apple" title="9. Apple App Store Review & Compliance">
                        <List dense sx={{ pl: 2 }}>
                            <ListItem disablePadding><ListItemText primary="For compliance with Apple’s App Store Review Guidelines, our applications must undergo an approval process." /></ListItem>
                            <ListItem disablePadding><ListItemText primary="Apple Reviewers may access the application for testing purposes. They will not retain user data beyond the review process." /></ListItem>
                        </List>
                    </Section>

                    <Section id="changes" title="10. Changes to This Privacy Policy">
                        We may update this Privacy Policy from time to time. Any updates will be posted on our website and in-app. Continued use of our applications after changes are made indicates acceptance of the updated policy.
                    </Section>

                    <Section id="contact" title="11. Contact Information">
                        If you have any questions about this Privacy Policy, you may contact us at:
                        <br />
                        Email: <Link href="mailto:wihan@mycharr.app" sx={{ color: '#cd99e3ff', textDecoration: 'none' }}>wihan@mycharr.app</Link><br />
                        Website: <Link href="https://www.mycharr.earth" target="_blank" rel="noopener noreferrer" sx={{ color: '#cd99e3ff', textDecoration: 'none' }}>www.mycharr.earth</Link>
                        <br /><br />
                        By using our applications, you acknowledge that you have read and understood this Privacy Policy.
                    </Section>
                </Grid>
            </Grid>
        </Grid>
    );
};

// --- Helper component for consistent spacing and scroll behavior ---
const Section = ({ id, title, children }) => (
    <Box
        id={id}
        sx={{
            mt: 10,
            mb: 10,
            scrollMarginTop: '100px',
        }}
    >
        <Typography variant="h6" gutterBottom fontWeight="bold">
            {title}
        </Typography>
        <Typography paragraph sx={{ lineHeight: 1.7 }}>
            {children}
        </Typography>
    </Box>
);